import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  CirclePlay,
  Rocket,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import "./ClosingShowcase.css";

const promises = [
  {
    title: "Turn ideas into products",
    copy: "From concept to launch",
    signal: "IDEAS → PRODUCTS",
    color: "#1edcff",
    icon: Rocket,
  },
  {
    title: "Work with a trusted team",
    copy: "Experienced, collaborative, reliable",
    signal: "PEOPLE → PROGRESS",
    color: "#e447ff",
    icon: UsersRound,
  },
  {
    title: "Build for real impact",
    copy: "Solutions that drive results",
    signal: "SYSTEMS → GROWTH",
    color: "#20e7d2",
    icon: BarChart3,
  },
  {
    title: "A partner for what’s next",
    copy: "Long-term support and growth",
    signal: "VISION → WHAT’S NEXT",
    color: "#21d8c4",
    icon: ShieldCheck,
  },
];

const metrics = [
  ["50+", "Projects delivered"],
  ["98%", "Client satisfaction"],
  ["4.8/5", "Average rating"],
  ["3+", "Years of impact"],
];

export default function ClosingShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activePromise, setActivePromise] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => document.body.classList.toggle("home-closing-showcase-active", entry.isIntersecting),
      { threshold: 0.42 },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      document.body.classList.remove("home-closing-showcase-active");
    };
  }, []);

  const handlePointerMove = (event: PointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    event.currentTarget.style.setProperty("--closing-x", `${x * 14}px`);
    event.currentTarget.style.setProperty("--closing-y", `${y * 10}px`);
    event.currentTarget.style.setProperty("--closing-glow-x", `${(x + 0.5) * 100}%`);
    event.currentTarget.style.setProperty("--closing-glow-y", `${(y + 0.5) * 100}%`);
  };

  const resetPointer = () => {
    sectionRef.current?.style.setProperty("--closing-x", "0px");
    sectionRef.current?.style.setProperty("--closing-y", "0px");
  };

  return (
    <section
      ref={sectionRef}
      id="build-together"
      data-testid="closing-showcase"
      className="edx-closing-showcase"
      aria-labelledby="closing-showcase-heading"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
      style={{ "--closing-accent": promises[activePromise].color } as CSSProperties}
    >
      <div className="edx-closing-scene" aria-hidden="true">
        <img src="/edenox-closing-founder-v1.png" alt="" />
      </div>
      <div className="edx-closing-shade" aria-hidden="true" />
      <div className="edx-closing-grid" aria-hidden="true" />
      <div className="edx-closing-energy" aria-hidden="true"><i /><i /><i /><i /></div>

      <div className="edx-closing-frame">
        <div className="edx-closing-content">
          <div className="edx-closing-kicker"><i /><span>LET’S BUILD TOGETHER</span></div>
          <h2 id="closing-showcase-heading">
            Ready to build<br />something that<br /><span>moves your<br />business forward?</span>
          </h2>
          <p>
            Whether you’re a startup with a bold idea, an established business looking to scale,
            or an organisation ready to modernise — EDENOX is here to turn your vision into real,
            measurable impact.
          </p>

          <div className="edx-closing-promises" role="list" aria-label="Why build with EDENOX">
            {promises.map((promise, index) => {
              const Icon = promise.icon;
              return (
                <button
                  key={promise.title}
                  type="button"
                  role="listitem"
                  className={`edx-closing-promise ${activePromise === index ? "is-active" : ""}`}
                  style={{ "--promise-color": promise.color } as CSSProperties}
                  onPointerEnter={() => setActivePromise(index)}
                  onFocus={() => setActivePromise(index)}
                  onClick={() => setActivePromise(index)}
                  data-testid={`closing-promise-${index}`}
                >
                  <Icon />
                  <strong>{promise.title}</strong>
                  <span>{promise.copy}</span>
                </button>
              );
            })}
          </div>

          <div className="edx-closing-actions">
            <Link to="/consultation" className="edx-closing-primary">Start a project <ArrowRight /></Link>
            <Link to="/case-studies" className="edx-closing-secondary"><CirclePlay /> See our work</Link>
          </div>

          <div className="edx-closing-metrics">
            {metrics.map(([value, label]) => (
              <div key={label}><b>{value}</b><span>{label}</span></div>
            ))}
          </div>
        </div>

        <div className="edx-closing-visual" aria-label="EDENOX founder working in a futuristic technology studio">
          <div className="edx-closing-panel edx-closing-panel-left">
            <span>GOOD</span><span>IDEAS</span><span>BUILD</span><span>GREATER</span><span>TOMORROWS</span><i />
          </div>
          <div className="edx-closing-panel edx-closing-panel-right">
            <span>DISCIPLINE</span><span>CREATIVE</span><span>PEOPLE</span><span>BUILD</span><span>EXTRAORDINARY</span><span>THINGS.</span><i />
          </div>
          <div className="edx-closing-signal" aria-live="polite">
            <Sparkles /><small>BUILD SIGNAL</small><strong>{promises[activePromise].signal}</strong>
          </div>
          <img className="edx-closing-laptop-logo" src="/edenox-logo.png" alt="EDENOX" />
          <div className="edx-closing-book-labels" aria-hidden="true">
            <span>STRATEGY</span><span>TECHNOLOGY</span><span>GROWTH</span><span>IMPACT</span>
          </div>
          <div className="edx-closing-signature"><i /> SAME IDEAS.<br />A BRIGHTER TOMORROW.</div>
        </div>
      </div>
    </section>
  );
}
